import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Overline, Spinner } from "@/components/Widgets";
import { CheckCircle, XCircle } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setStatus("paid");
        if (["failed", "expired"].includes(data.payment_status)) return setStatus("failed");
      } catch (e) { /* keep polling */ }
      if (attempts.current >= 10) return setStatus("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params]);

  return (
    <div className="max-w-md mx-auto mt-16 fade-up">
      <Card className="text-center p-10" testid="payment-success-card">
        {status === "checking" && (
          <>
            <div className="flex justify-center mb-4"><Spinner label="Confirming your payment…" /></div>
            <p className="text-sm text-muted-foreground">Please wait while we activate your subscription.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={56} weight="fill" className="text-emerald-400 mx-auto mb-4" />
            <h2 className="font-head font-bold text-2xl">You're subscribed!</h2>
            <p className="text-sm text-muted-foreground mt-2">Your SMI subscription is active. Time to dominate your reputation.</p>
            <button data-testid="go-dashboard-btn" onClick={() => navigate("/")}
              className="mt-6 bg-primary text-primary-foreground font-semibold px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors duration-200">Go to dashboard</button>
          </>
        )}
        {["failed", "timeout", "error"].includes(status) && (
          <>
            <XCircle size={56} weight="fill" className="text-destructive mx-auto mb-4" />
            <h2 className="font-head font-bold text-2xl">Payment not confirmed</h2>
            <p className="text-sm text-muted-foreground mt-2">
              {status === "timeout" ? "It's taking longer than expected. Check billing shortly." : "We couldn't confirm your payment."}
            </p>
            <button data-testid="back-billing-btn" onClick={() => navigate("/billing")}
              className="mt-6 border border-border px-5 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors duration-200">Back to billing</button>
          </>
        )}
      </Card>
    </div>
  );
}
